import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * The canonical panel around a chart: bordered card, title row, optional
 * actions/legend slots.
 *
 * Consolidates what `lib-tracker-graphs` hand-rolled per chart, and gives the
 * monitoring charts the surface they never had — they used to render bare on the
 * page background with the title baked into the canvas, where it could not be
 * translated, truncated, or themed.
 *
 * Slots:
 *  - `[card-actions]` — buttons on the title row (right).
 *  - `[card-legend]`  — an HTML legend under the title, above the chart.
 *  - default          — the chart itself.
 */
@Component({
  selector: 'wh-chart-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block min-w-0' },
  template: `
    <div
      class="flex h-full min-w-0 flex-col rounded-xl border border-gray-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
      @if (title() || subtitle()) {
        <div class="mb-2 flex min-w-0 items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="wh-display truncate text-sm font-semibold text-gray-900 dark:text-slate-100" [title]="title()">
              {{ title() }}
            </div>
            @if (subtitle(); as s) {
              <div class="truncate text-xs text-gray-500 dark:text-slate-400" [title]="s">{{ s }}</div>
            }
          </div>
          <div class="flex shrink-0 items-center gap-1">
            <ng-content select="[card-actions]" />
          </div>
        </div>
      }

      <ng-content select="[card-legend]" />

      <div class="relative min-h-0 flex-1">
        <ng-content />

        @if (loading()) {
          <div class="absolute inset-0 flex items-center justify-center rounded-lg bg-white/70 dark:bg-slate-800/70">
            <span class="animate-pulse text-sm text-gray-400 dark:text-slate-500">{{ loadingLabel() }}</span>
          </div>
        } @else if (empty()) {
          <!-- Overlaid rather than swapped in: the chart canvas must keep its size
               so ECharts does not resize to 0 and lose its layout on first data. -->
          <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span
              class="rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-400 dark:border-slate-600 dark:text-slate-500">
              {{ emptyLabel() }}
            </span>
          </div>
        }
      </div>
    </div>
  `,
})
export class WhChartCardComponent {
  readonly title = input<string>('');
  readonly subtitle = input<string>('');
  readonly loading = input<boolean>(false);
  /** Shows the empty hint over the chart. Callers pass "no data received yet". */
  readonly empty = input<boolean>(false);
  readonly loadingLabel = input<string>('Loading…');
  readonly emptyLabel = input<string>('No data');
}
